/**
 * Zugriff auf Aufgaben, live aus der Datenbank.
 *
 * Der Bereichsfilter wird hier angewendet und nicht in den Komponenten -
 * so vergisst ihn keine Liste.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';

import { db } from '@/db/schema';
import { useAreaFilter } from '@/store/areaFilter';
import type { LocalDate, Task } from '@/domain/types';

function useFiltered(tasks: Task[]): Task[] {
  const { activeAreaIds } = useAreaFilter();
  return useMemo(() => {
    if (activeAreaIds.length === 0) return tasks;
    return tasks.filter((t) => t.areaId != null && activeAreaIds.includes(t.areaId));
  }, [tasks, activeAreaIds]);
}

/** Alles, was noch nicht eingeordnet wurde. */
export function useInbox(): Task[] {
  const rows = useLiveQuery(
    () => db.tasks.where('status').equals('inbox').sortBy('order'),
    [],
    [] as Task[],
  );
  return useFiltered(rows);
}

/** Offene Aufgaben ausserhalb der Inbox - die Grundlage der Eisenhower-Matrix. */
export function useOpenTasks(): Task[] {
  const rows = useLiveQuery(
    () => db.tasks
      .where('status').anyOf('naechste', 'wartet', 'geplant')
      .sortBy('order'),
    [],
    [] as Task[],
  );
  return useFiltered(rows);
}

/** Alles Unerledigte, auch die Inbox - fuer "heute" und "ueberfaellig" auf der Uebersicht (V-01). */
export function useUnerledigt(): Task[] {
  const rows = useLiveQuery(
    () => db.tasks.where('status').notEqual('erledigt').sortBy('order'),
    [],
    [] as Task[],
  );
  return useFiltered(rows);
}

/** Aufgaben eines bestimmten Tages, Big 3 zuerst. */
export function usePlannedFor(date: LocalDate): Task[] {
  const rows = useLiveQuery(
    () => db.tasks.where('plannedDate').equals(date).sortBy('order'),
    [date],
    [] as Task[],
  );
  const filtered = useFiltered(rows);

  return useMemo(
    () => [...filtered].sort((a, b) =>
      Number(Boolean(b.isBigThree)) - Number(Boolean(a.isBigThree))
      || Number(a.status === 'erledigt') - Number(b.status === 'erledigt')
      || a.order - b.order),
    [filtered],
  );
}

export function useTaskCounts(): { inbox: number; heute: number } {
  return useLiveQuery(
    async () => ({
      inbox: await db.tasks.where('status').equals('inbox').count(),
      heute: 0,
    }),
    [],
    { inbox: 0, heute: 0 },
  );
}

/**
 * Offene Aufgaben, die noch nirgends haengen - weder an einem Projekt noch an
 * einem Ziel. Die Kandidaten fuer "Aufgabe zuordnen" auf Projekt- und
 * Zielkarten (Roadmap F-11: Zuordnung von beiden Seiten).
 */
export function useUnlinkedOpenTasks(): Task[] {
  const rows = useLiveQuery(
    () => db.tasks.filter((t) => !t.projectId && !t.goalId && t.status !== 'erledigt').toArray(),
    [],
    [] as Task[],
  );
  return useMemo(() => [...rows].sort((a, b) => a.title.localeCompare(b.title, 'de')), [rows]);
}
